import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { CheckCircle2, LogOut, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const CAREER_PREFERENCES = [
  { value: "train-driver", label: "Train driver" },
  { value: "conductor", label: "Conductor" },
  { value: "depot-driver", label: "Depot driver" },
  { value: "maintenance-rolling-stock", label: "Maintenance (Rolling Stock)" },
  { value: "maintenance-track-infrastructure", label: "Maintenance (Track or Infrastructure)" },
  { value: "administrative", label: "Administrative" },
  { value: "graduate", label: "Graduate" },
  { value: "signalling", label: "Signalling" },
  { value: "other", label: "Other" },
];

const STAGE_LABELS = {
  interest_registered: "Interest registered",
  email_verified: "Email verified",
  email_verified_pending_password: "Pending password setup",
  stage_one_passed: "Stage 1 — Basic Eligibility",
  stage_one_hold: "Stage 1 — Basic Eligibility (Hold)",
  stage_two_behavioural_profiling: "Stage 2 — Operational Behaviour Assessment",
  stage_two_target_role_selection: "Stage 2 — Operational Behaviour Assessment",
  stage_two: "Stage 2 — Operational Behaviour Assessment",
  stage_three_aptitude_tests: "Stage 3 — Scenario Aptitude Tests",
  stage_three: "Stage 3 — Scenario Aptitude Tests",
  stage_four_cv_submission: "Stage 4 — CV Submission",
  stage_five_simulated_interview: "Stage 5 — Simulated Interview",
  stage_six_recorded_interview: "Stage 6 — 1:1 Recorded Interview (Teams)",
};

// Ordered pipeline stages shown as pills to the candidate.
const STAGE_PIPELINE = [
  { key: "stage_one_passed", label: "1. Basic Eligibility", description: "Stage 1 checks your baseline eligibility and readiness for rail recruitment." },
  { key: "stage_two_behavioural_profiling", label: "2. Operational Behaviour Assessment", description: "Stage 2 assesses operational judgement and safety behaviour through timed workplace scenarios." },
  { key: "stage_three_aptitude_tests", label: "3. Aptitude Tests", description: "Stage 3 runs scenario-based aptitude tests tailored to your selected role route." },
  { key: "stage_four_cv_submission", label: "4. CV Submission", description: "Stage 4 requires CV submission for employer review and matching context." },
  { key: "stage_five_simulated_interview", label: "5. Simulated Interview", description: "Stage 5 simulates interview conditions to assess communication and fit." },
  { key: "stage_six_recorded_interview", label: "6. Teams Interview", description: "Stage 6 is a 1:1 recorded interview over Microsoft Teams." },
];

// Returns the 0-based pipeline index for a raw stage key.
const stagePipelineIndex = (key) => {
  switch ((key || "").toLowerCase()) {
    case "stage_six_recorded_interview":
      return 5;
    case "stage_five_simulated_interview":
      return 4;
    case "stage_four_cv_submission":
      return 3;
    case "stage_three_aptitude_tests":
    case "stage_three":
      return 2;
    case "stage_two_behavioural_profiling":
    case "stage_two_target_role_selection":
    case "stage_two":
      return 1;
    case "interest_registered":
    case "email_verified":
    case "email_verified_pending_password":
    case "stage_one_hold":
    case "stage_one_passed":
    default:
      return 0;
  }
};

const ADMIN_STATUS_LABELS = {
  new: "New",
  reviewing: "Reviewing",
  on_hold: "On hold",
  approved: "Approved",
  active: "Active",
  shortlist_ready: "Shortlist ready",
  paused: "Paused",
  rejected: "Rejected",
};

const adminBadgeClass = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "active" || key === "approved" || key === "shortlist_ready") return "bg-lime-100 text-lime-900 border-lime-300";
  if (key === "reviewing" || key === "new") return "bg-sky/10 text-ink border-sky/30";
  if (key === "on_hold" || key === "paused") return "bg-amber/20 text-ink border-amber/50";
  if (key === "rejected") return "bg-red-100 text-red-900 border-red-300";
  return "bg-paper text-ink border-border";
};

export default function TalentPoolDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [prefRoles, setPrefRoles] = useState([]);
  const [prefPostcode, setPrefPostcode] = useState("");
  const [prefLocations, setPrefLocations] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [inappThreads, setInappThreads] = useState([]);
  const [inappArchivedThreads, setInappArchivedThreads] = useState([]);
  const [inappActiveThread, setInappActiveThread] = useState(null);
  const [inappMessages, setInappMessages] = useState([]);
  const [inappReplyBody, setInappReplyBody] = useState("");
  const [inappReplySending, setInappReplySending] = useState(false);
  const [selectedStageIdx, setSelectedStageIdx] = useState(0);

  const effectiveProfile = profile || user || {};
  const checks = effectiveProfile?.talent_pool_stage_one?.checks || effectiveProfile?.gateway_checks || {};
  const blockers = effectiveProfile?.talent_pool_stage_one?.hard_blocker_failures || [];
  const stageKey = (effectiveProfile?.talent_pool_stage || "").trim().toLowerCase();
  const adminStatus = ADMIN_STATUS_LABELS[(effectiveProfile?.talent_pool_admin_status || "").trim().toLowerCase()] || effectiveProfile?.talent_pool_admin_status || "—";
  const stageOnePassed = blockers.length === 0;
  const unreadInappCount = (inappThreads || []).filter((t) => !t.recipient_read_at).length;
  const profileNeedsCompletion = prefRoles.length === 0 || !prefPostcode.trim();

  const signOut = () => {
    logout();
    nav("/");
  };

  const refreshInapp = async () => {
    const [openRows, archivedRows] = await Promise.all([
      api.get("/inapp/threads", { params: { archived: false, limit: 300 } }).catch(() => ({ data: [] })),
      api.get("/inapp/threads", { params: { archived: true, limit: 300 } }).catch(() => ({ data: [] })),
    ]);
    setInappThreads(openRows.data || []);
    setInappArchivedThreads(archivedRows.data || []);
  };

  const loadProfile = async () => {
    try {
      const r = await api.get("/talent-pool/profile");
      const p = r.data || {};
      setProfile(p);
      setPrefRoles(Array.isArray(p.target_roles) ? p.target_roles : []);
      setPrefPostcode(p.preferred_postcode || "");
      setPrefLocations(p.preferred_locations || "");
    } catch {
      setPrefRoles(Array.isArray(user?.target_roles) ? user.target_roles : []);
      setPrefPostcode(user?.preferred_postcode || "");
      setPrefLocations(user?.preferred_locations || "");
    }
  };

  const toggleRole = (role) => {
    setPrefRoles((prev) => (
      prev.includes(role)
        ? prev.filter((r) => r !== role)
        : [...prev, role]
    ));
  };

  const savePreferences = async () => {
    if (prefRoles.length === 0) {
      toast.error("Select at least one career preference");
      return;
    }
    if (!prefPostcode.trim()) {
      toast.error("Postcode is required");
      return;
    }
    setProfileSaving(true);
    try {
      const r = await api.put("/talent-pool/profile", {
        target_roles: prefRoles,
        postcode: prefPostcode,
        preferred_locations: prefLocations || null,
      });
      setProfile(r.data || null);
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save preferences");
    } finally {
      setProfileSaving(false);
    }
  };

  useEffect(() => {
    loadProfile();
    refreshInapp();
  }, []);

  useEffect(() => {
    const idx = stagePipelineIndex(stageKey);
    setSelectedStageIdx(idx);
  }, [stageKey]);

  const loadInappThread = async (threadId) => {
    try {
      const r = await api.get(`/inapp/threads/${threadId}/messages`);
      setInappActiveThread(r.data.thread);
      setInappMessages(r.data.messages || []);
      await api.post(`/inapp/threads/${threadId}/read`, {});
      refreshInapp();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load thread");
    }
  };

  const replyInappThread = async () => {
    if (!inappActiveThread?.id) return;
    if (!inappReplyBody.trim()) { toast.error("Reply cannot be empty"); return; }
    setInappReplySending(true);
    try {
      await api.post(`/inapp/threads/${inappActiveThread.id}/reply`, { body: inappReplyBody, format: "text" });
      setInappReplyBody("");
      await loadInappThread(inappActiveThread.id);
      toast.success("Reply sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reply failed");
    } finally {
      setInappReplySending(false);
    }
  };

  const archiveInappThread = async (threadId, archived = true) => {
    try {
      await api.post(`/inapp/threads/${threadId}/archive`, { archived });
      if (inappActiveThread?.id === threadId) {
        setInappActiveThread(null);
        setInappMessages([]);
      }
      refreshInapp();
      toast.success(archived ? "Archived" : "Moved to inbox");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update archive state");
    }
  };

  return (
    <div className="min-h-screen bg-sand">
      <header className="bg-navy text-bone">
        <div className="livery-stripe h-1" />
        <div className="max-w-5xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div>
            <Link to="/" className="font-display font-bold text-sm">Railway Careers UK · Talent Pool</Link>
            <div className="text-[11px] text-bone/70 mt-0.5">{unreadInappCount} unread notifications</div>
          </div>
          <Button size="sm" variant="outline" onClick={signOut} className="border-bone/30 text-bone hover:bg-cream/10" data-testid="tp-dash-logout">
            <LogOut className="w-3.5 h-3.5 mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        <div className="text-xs uppercase tracking-[0.2em] text-ink font-semibold">Talent Pool Dashboard</div>
        <h1 className="font-display font-extrabold text-ink text-3xl md:text-4xl tracking-tight mt-1">
          Welcome, {user?.first_name || user?.contact_name || "Candidate"}
        </h1>
        <p className="text-muted-foreground mt-2">
          This dashboard shows your progress through the 6-stage Talent Pool journey.
        </p>

        <div className="mt-6 bg-card border border-border rounded-lg p-6" data-testid="tp-dash-status">
          <div className="text-xs uppercase tracking-[0.16em] text-ink font-semibold">Candidate Progress</div>
          <p className="text-sm text-muted-foreground mt-1">
            You progress to the next stage only after admin approves your current stage.
          </p>

          {/* Stage progress rail + cards */}
          <div className="mt-5" data-testid="tp-dash-pipeline">
            <div className="rounded-2xl border border-border/80 bg-gradient-to-r from-sky-50 via-paper to-lime-50 p-4">
              <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                <span>Journey progress</span>
                <span>
                  Stage {Math.min(stagePipelineIndex(stageKey) + 1, STAGE_PIPELINE.length)} / {STAGE_PIPELINE.length}
                </span>
              </div>

              <div className="mt-3 hidden md:block">
                <div className="h-2 rounded-full bg-white/70 border border-border/60 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-sky-500 via-navy to-lime-500 transition-all duration-500"
                    style={{ width: `${Math.max(8, ((stagePipelineIndex(stageKey) + 1) / STAGE_PIPELINE.length) * 100)}%` }}
                  />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-2">
                {STAGE_PIPELINE.map((stage, idx) => {
                  const currentIdx = stagePipelineIndex(stageKey);
                  const isHold = stageKey === "stage_one_hold" && idx === 0;
                  const isCompleted = idx < currentIdx;
                  const isActive = idx === currentIdx && !isHold;
                  const isSelectable = idx <= currentIdx;
                  const isSelected = idx === selectedStageIdx;

                  let cardClasses = "border-border bg-background/80 text-muted-foreground";
                  if (isCompleted) cardClasses = "border-lime-300 bg-lime-50 text-lime-900";
                  if (isActive) cardClasses = "border-navy bg-navy text-bone";
                  if (isHold) cardClasses = "border-amber/60 bg-amber/15 text-amber-900";

                  return (
                    <button
                      key={stage.key}
                      type="button"
                      onClick={() => {
                        if (isSelectable) setSelectedStageIdx(idx);
                      }}
                      disabled={!isSelectable}
                      className={`relative rounded-xl border px-3 py-3 text-left transition-all ${cardClasses} ${isSelected ? "ring-2 ring-amber/50" : ""} ${!isSelectable ? "opacity-55 cursor-not-allowed" : "hover:shadow-sm"}`}
                      data-testid={`tp-stage-pill-${stage.key}`}
                      title={stage.description}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-mono">{idx + 1}</span>
                        {isCompleted && <CheckCircle2 className="w-4 h-4 shrink-0" />}
                        {isHold && <AlertTriangle className="w-4 h-4 shrink-0" />}
                      </div>
                      <div className="mt-2 text-xs font-semibold leading-snug">{stage.label}</div>
                      {isActive && (
                        <span className="absolute -top-1.5 -right-1.5 flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber opacity-75" />
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-amber" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Active stage description */}
          <p className="mt-4 text-sm text-muted-foreground" data-testid="tp-dash-stage-description">
            {stageKey === "stage_one_hold" && selectedStageIdx === 0
              ? "You are currently on hold at Basic Eligibility until the mandatory checks are resolved."
              : (STAGE_PIPELINE[selectedStageIdx]?.description || "")}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Stage selection here is view-only and does not change your progression status.
          </p>

          {stageKey === "stage_one_hold" && (
            <div className="mt-3 rounded-md border border-amber/40 bg-amber/10 p-3 text-sm text-ink" data-testid="tp-dash-stage1-hold">
              You are currently on hold at Stage 1 and cannot progress to Stage 2 yet. Please review the mandatory requirements below.
            </div>
          )}

          {/* Admin status chip — smaller, secondary */}
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">Admin status:</span>
            <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${adminBadgeClass(effectiveProfile?.talent_pool_admin_status)}`}>
              {adminStatus}
            </span>
          </div>

          {stageKey !== "stage_one_hold" && effectiveProfile?.stage_two_access_enabled && (
            <div className="mt-4">
              <Link
                to="/talent-pool/stage-2"
                className="inline-flex items-center rounded-full bg-navy px-4 py-2 text-sm font-semibold text-bone hover:bg-lime-600 hover:text-ink"
                data-testid="tp-dash-stage2-start"
              >
                Open Operational Behaviour Assessment
              </Link>
            </div>
          )}
        </div>

        <div className="mt-6 bg-card border border-border rounded-lg p-6" data-testid="tp-dash-preferences">
          <div className="font-display font-bold text-ink text-xl">Career preferences and location</div>
          <p className="text-sm text-muted-foreground mt-2">
            Select one or more career preferences and keep your postcode up to date so recruiters can match you by role and distance.
          </p>
          <div className="mt-3 rounded-md border border-sky/30 bg-sky/10 px-3 py-2 text-xs text-ink">
            Your profile is visible only to verified, approved employers on Railway Careers UK.
          </div>
          {profileNeedsCompletion ? (
            <div className="mt-3 rounded-md border border-amber/40 bg-amber/10 px-3 py-2 text-sm text-ink">
              Complete this section to keep your profile visible for role and distance matching.
            </div>
          ) : null}

          <div className="mt-4 grid md:grid-cols-2 gap-2 text-sm">
            {CAREER_PREFERENCES.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors ${prefRoles.includes(opt.value) ? "border-amber/60 bg-amber/10" : "border-border bg-background hover:bg-paper/50"}`}
              >
                <input
                  type="checkbox"
                  checked={prefRoles.includes(opt.value)}
                  onChange={() => toggleRole(opt.value)}
                  data-testid={`tp-preference-${opt.value}`}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>

          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <div>
              <Label>Postcode</Label>
              <Input
                className="mt-1 uppercase"
                placeholder="M1 2AB"
                value={prefPostcode}
                onChange={(e) => setPrefPostcode(e.target.value)}
                data-testid="tp-preference-postcode"
              />
            </div>
            <div>
              <Label>Preferred locations (optional)</Label>
              <Input
                className="mt-1"
                placeholder="Manchester, Liverpool"
                value={prefLocations}
                onChange={(e) => setPrefLocations(e.target.value)}
                data-testid="tp-preference-locations"
              />
            </div>
          </div>

          <div className="mt-4">
            <Button
              onClick={savePreferences}
              disabled={profileSaving}
              className="bg-navy text-bone hover:bg-lime-600 hover:text-ink"
              data-testid="tp-preferences-save"
            >
              {profileSaving ? "Saving..." : "Save preferences"}
            </Button>
          </div>
        </div>

        <div className="mt-6 bg-card border border-border rounded-lg p-6" data-testid="tp-dash-checks">
          <div className="font-display font-bold text-ink text-xl">Stage One mandatory checks</div>
          <div className="mt-4 grid md:grid-cols-2 gap-3 text-sm">
            {[
              ["right_to_work_uk", checks.right_to_work_uk],
              ["english_communication", checks.english_communication],
              ["understands_shift_patterns", checks.understands_shift_patterns],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle2 className={`w-4 h-4 ${v ? "text-lime-600" : "text-amber-700"}`} />
                <span>{k.replaceAll("_", " ")}</span>
              </div>
            ))}
          </div>
          {blockers.length > 0 && (
            <div className="mt-4 text-sm text-amber-800">
              Missing mandatory checks: {blockers.join(", ").replaceAll("_", " ")}
            </div>
          )}
          {blockers.length === 0 && (
            <div className="mt-4 inline-flex items-center rounded-full border border-lime-300 bg-lime-100 px-3 py-1 text-xs font-semibold text-lime-900">
              Stage 1 mandatory checks complete
            </div>
          )}
        </div>

        <div className="mt-6 grid lg:grid-cols-2 gap-6" data-testid="tp-inapp-section">
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border bg-sand text-xs uppercase tracking-wider text-muted-foreground">Inbox notifications</div>
            <div className="max-h-[260px] overflow-auto">
              {inappThreads.length === 0 ? (
                <div className="p-5 text-sm text-muted-foreground">No notifications in your inbox.</div>
              ) : inappThreads.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => loadInappThread(t.id)}
                  className="w-full text-left px-4 py-3 border-b border-border hover:bg-paper/40"
                  data-testid={`tp-inapp-thread-${t.id}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-semibold text-ink truncate">{t.subject}</div>
                    {!t.recipient_read_at && <span className="text-[10px] px-2 py-0.5 rounded bg-amber/30 text-ink font-semibold">Unread</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 truncate">{t.last_message_preview || "—"}</div>
                </button>
              ))}
            </div>
            <div className="px-4 py-3 border-t border-border bg-paper/30">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Archive</div>
              <div className="mt-2 max-h-[120px] overflow-auto space-y-2">
                {inappArchivedThreads.length === 0 ? (
                  <div className="text-xs text-muted-foreground">No archived notifications.</div>
                ) : inappArchivedThreads.map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="text-muted-foreground truncate">{t.subject}</span>
                    <Button size="sm" variant="outline" onClick={() => archiveInappThread(t.id, false)} data-testid={`tp-inapp-unarchive-${t.id}`}>Restore</Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            {!inappActiveThread ? (
              <div className="text-sm text-muted-foreground">Select a notification thread to read and reply.</div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display font-bold text-ink text-lg">{inappActiveThread.subject}</div>
                    <div className="text-xs text-muted-foreground mt-1">Updated: {(inappActiveThread.updated_at || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => archiveInappThread(inappActiveThread.id, true)} data-testid="tp-inapp-archive">Archive</Button>
                </div>
                <div className="mt-3 max-h-[280px] overflow-auto border border-border rounded-lg bg-background p-3 space-y-3">
                  {inappMessages.map((m) => (
                    <div key={m.id} className={`rounded-lg px-3 py-2 text-sm ${m.sender_role === "admin" ? "bg-sky/10 border border-sky/30" : "bg-paper border border-border"}`}>
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{m.sender_role === "admin" ? "Admin" : "You"}</div>
                      <div className="mt-1 whitespace-pre-wrap">{m.body_text || ""}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <Label>Reply</Label>
                  <Textarea className="mt-1 min-h-[90px]" value={inappReplyBody} onChange={(e) => setInappReplyBody(e.target.value)} data-testid="tp-inapp-reply" />
                  <div className="mt-2 flex gap-2">
                    <Button onClick={replyInappThread} disabled={inappReplySending} className="bg-amber text-ink hover:bg-amber-600" data-testid="tp-inapp-reply-send">{inappReplySending ? "Sending..." : "Send reply"}</Button>
                    <Button variant="outline" onClick={() => loadInappThread(inappActiveThread.id)} data-testid="tp-inapp-refresh">Refresh</Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}